import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { addDays, clampDate, displayDate, FIRST_DATE, inDateRange, LAST_DATE, localToday, monthDays, moveMonth, validDate } from "./calendarDate";
import "./datePicker.css";

type DatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  id?: string;
  name?: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  displayValue?: string;
};

export function DatePicker({ value, onChange, label, id, name, min, max, required = false, disabled = false, clearable = !required, displayValue }: DatePickerProps) {
  const lowerBound = min && validDate(min) ? min : FIRST_DATE;
  const upperBound = max && validDate(max) ? max : LAST_DATE;
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  function choose(next: string) {
    if (disabled || trigger.current?.matches(":disabled")) return;
    setInvalid(false);
    onChange(next);
    setOpen(false);
  }
  const error = required && !value ? "Select a date." : "Select a date within the available range.";
  return <span className="date-picker-field">
    <button id={triggerId} ref={trigger} type="button" className="date-picker-trigger" disabled={disabled}
      aria-label={`${label}: ${displayDate(value) || "Not selected"}`} aria-haspopup="dialog" aria-expanded={open}
      aria-invalid={invalid || undefined} aria-describedby={invalid ? `${generatedId}-error` : undefined}
      onClick={() => setOpen(true)}>
      <span className={!value ? "date-picker-placeholder" : undefined}>{displayValue || displayDate(value) || "Select date"}</span>
      <CalendarDays size={16} aria-hidden="true" />
    </button>
    {/* Keep native form validation and submission without exposing a format placeholder. */}
    <input className="date-picker-validation" type="date" name={name} value={value} onChange={event => choose(event.target.value)}
      min={lowerBound} max={upperBound} required={required} disabled={disabled} tabIndex={-1} aria-hidden="true"
      onInvalid={event => { event.preventDefault(); setInvalid(true); trigger.current?.focus(); }} />
    {invalid ? <span id={`${generatedId}-error`} className="date-picker-error" role="alert">{error}</span> : null}
    {open && !disabled ? <CalendarDialog value={value} label={label} min={lowerBound} max={upperBound} clearable={clearable}
      onChange={choose} onClose={() => setOpen(false)} /> : null}
  </span>;
}

const months = Array.from({ length: 12 }, (_, month) => new Date(Date.UTC(2026, month, 1)).toLocaleDateString("en-IN", { month: "long", timeZone: "UTC" }));
const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function CalendarDialog({ value, label, min = FIRST_DATE, max = LAST_DATE, clearable, onChange, onClose }: Pick<DatePickerProps, "value" | "label" | "min" | "max" | "clearable" | "onChange"> & { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [focused, setFocused] = useState(() => clampDate(value, min, max));
  const [month, setMonth] = useState(() => clampDate(value, min, max).slice(0, 7));
  const [yearInput, setYearInput] = useState(month.slice(0, 4));
  const [focusDay, setFocusDay] = useState(false);
  const today = localToday();
  const days = monthDays(`${month}-01`);
  useEffect(() => {
    const element = dialog.current!;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    element.showModal();
    element.querySelector<HTMLElement>("[data-calendar-active=true]")?.focus();
    return () => { element.close(); if (opener?.isConnected) opener.focus(); };
  }, []);
  useLayoutEffect(() => {
    if (focusDay) dialog.current?.querySelector<HTMLElement>("[data-calendar-active=true]")?.focus();
  }, [focused, month, focusDay]);
  function move(next: string, focus = true) {
    const day = next.length > 10 ? max : next < FIRST_DATE ? min : next > LAST_DATE ? max : clampDate(next, min, max);
    setFocused(day); setMonth(day.slice(0, 7)); setYearInput(day.slice(0, 4)); setFocusDay(focus);
  }
  function keyboard(event: KeyboardEvent<HTMLButtonElement>, day: string) {
    const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
    let next: string;
    switch (event.key) {
      case "ArrowLeft": next = addDays(day, -1); break;
      case "ArrowRight": next = addDays(day, 1); break;
      case "ArrowUp": next = addDays(day, -7); break;
      case "ArrowDown": next = addDays(day, 7); break;
      case "Home": next = addDays(day, -weekday); break;
      case "End": next = addDays(day, 6 - weekday); break;
      case "PageUp": next = moveMonth(day, event.shiftKey ? -12 : -1); break;
      case "PageDown": next = moveMonth(day, event.shiftKey ? 12 : 1); break;
      default: return;
    }
    event.preventDefault(); move(next);
  }
  return createPortal(<dialog ref={dialog} className="date-picker-dialog" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); event.stopPropagation(); onClose(); }}
    onKeyDown={event => event.stopPropagation()} onClick={event => { event.stopPropagation(); if (event.target === event.currentTarget) onClose(); }}>
    <header className="date-picker-header"><h2 id={titleId}>{label}</h2><button type="button" className="date-picker-icon" aria-label="Close calendar" onClick={onClose}><X size={18} aria-hidden="true" /></button></header>
    <div className="date-picker-month-nav">
      <button type="button" className="date-picker-icon" aria-label="Previous month" disabled={month <= min.slice(0, 7)} onClick={() => move(moveMonth(focused, -1), false)}><ChevronLeft size={18} aria-hidden="true" /></button>
      <select aria-label="Month" value={Number(month.slice(5)) - 1} onChange={event => move(moveMonth(focused, Number(event.target.value) - Number(month.slice(5)) + 1), false)}>
        {months.map((name, index) => <option key={name} value={index} disabled={`${month.slice(0, 4)}-${String(index + 1).padStart(2, "0")}` < min.slice(0, 7) || `${month.slice(0, 4)}-${String(index + 1).padStart(2, "0")}` > max.slice(0, 7)}>{name}</option>)}
      </select>
      <input aria-label="Year" type="number" min={min.slice(0, 4)} max={max.slice(0, 4)} value={yearInput}
        onChange={event => { setYearInput(event.target.value); if (/^[1-9]\d{3}$/.test(event.target.value)) move(moveMonth(focused, (Number(event.target.value) - Number(month.slice(0, 4))) * 12), false); }}
        onBlur={() => setYearInput(month.slice(0, 4))} />
      <button type="button" className="date-picker-icon" aria-label="Next month" disabled={month >= max.slice(0, 7)} onClick={() => move(moveMonth(focused, 1), false)}><ChevronRight size={18} aria-hidden="true" /></button>
    </div>
    <span className="date-picker-announcement" aria-live="polite">{months[Number(month.slice(5)) - 1]} {month.slice(0, 4)}</span>
    <table className="date-picker-calendar" role="grid" aria-label="Choose a date">
      <thead><tr>{weekdays.map(day => <th key={day} scope="col">{day}</th>)}</tr></thead>
      <tbody>{Array.from({ length: 6 }, (_, row) => <tr key={row}>{days.slice(row * 7, row * 7 + 7).map(day => <td key={day} aria-selected={day === value}>
        <button type="button" disabled={!inDateRange(day, min, max)} tabIndex={day === focused ? 0 : -1} data-calendar-active={day === focused}
          className={`date-picker-day${day.slice(0, 7) !== month ? " date-picker-other-month" : ""}${day === value ? " date-picker-selected" : ""}`}
          aria-label={displayDate(day, true)} aria-current={day === today ? "date" : undefined}
          onFocus={() => setFocused(day)} onKeyDown={event => keyboard(event, day)} onClick={() => onChange(day)}>{Number(day.slice(-2))}</button>
      </td>)}</tr>)}</tbody>
    </table>
    <footer className="date-picker-footer">
      {clearable ? <button type="button" disabled={!value} onClick={() => onChange("")}>Clear date</button> : <span />}
      <button type="button" disabled={!inDateRange(today, min, max)} onClick={() => onChange(today)}>Today</button>
    </footer>
  </dialog>, document.body);
}
